import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { DirtyFlagService } from '@ddv/behaviors';
import { AlertService, MultiSubscriptionComponent } from '@ddv/common-components';
import { DashboardFiltersService } from '@ddv/dashboards';
import { Datasource, DataWrapper, MetadataService, WidgetDataSourceService } from '@ddv/datasets';
import { QueryParamsService } from '@ddv/filters';
import { ManagerService, SelectedWidgetAction } from '@ddv/layout';
import {
    AdditionalFilter,
    AppWidgetState,
    Client,
    DashboardFilter,
    EXCLUDE,
    FILTER_TYPE,
    FilterCriteria,
    FilterPreference,
    FuzzyDates,
    getAvailableCriteriaOptions,
    getAvailableOperators,
    MetadataLookup,
    UserPreferences,
    WidgetData,
    WidgetIdNamedQueryId,
} from '@ddv/models';
import { compareSortOptions, deepClone, deepCompare } from '@ddv/utils';
import { RANK_SUFFIX } from '@ddv/visualizations';
import { DdvWidgetService } from '@ddv/widget-on-boards';
import { FilterableField, FilterSelection } from '@hs/ui-core-additional-filters';
import { DropdownOption } from '@hs/ui-core-common';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund } from '@hs/ui-core-fund';
import { ModalOptions } from '@hs/ui-core-layout';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

interface Filter {
    name: string;
    value: string | number | boolean | undefined;
    isMasterWidgetFilter: boolean;
    isDetailWidgetFilter?: boolean;
}

@Component({
    selector: 'app-filters-bar',
    templateUrl: './filters-bar.component.html',
    styleUrls: ['./filters-bar.component.scss'],
    standalone: false,
})
export class FiltersBarComponent extends MultiSubscriptionComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() dashboardId: string | undefined;
    @Input() inPresentationMode = false;
    @Input() widgetId: number | undefined;
    @Input() fuzzyDates: FuzzyDates = new FuzzyDates();
    @Input() ccFuzzyDates: FuzzyDate[] = [];
    @Input() clients: Client[] = [];
    @Input() funds: Fund[] = [];
    @Input() userPreferences: UserPreferences | undefined;

    @Output() filtersRemoved = new EventEmitter<string[]>();

    private readonly queryParamsService = inject(QueryParamsService);
    private readonly widgetDataSourceService = inject(WidgetDataSourceService);
    private readonly dirtyFlagCheck = inject(DirtyFlagService);
    private readonly dashboardFiltersService = inject(DashboardFiltersService);
    private readonly metadataService = inject(MetadataService);
    private readonly manager = inject(ManagerService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly ddvWidgetService = inject(DdvWidgetService);
    private readonly alertService = inject(AlertService);
    private readonly router = inject(Router);

    subscriptionState: 'All Subscribed' | 'Selected Widget' | undefined;
    filters: Filter[] = [];
    selectedWidgetFilters: FilterPreference | undefined;
    filterAttributeList: FilterableField[] = [];

    protected selectedFilterAttributes: FilterSelection[] | undefined;
    protected noFiltersSelected = 'Add Filter';
    protected modalOptions: ModalOptions = {};
    protected showWidgetFilters = false;
    protected selectedWidgetId: number | undefined;

    private deltas: AdditionalFilter[] | undefined;
    private paramsSubscription: Subscription | undefined;
    private widgetPrefs: AppWidgetState | undefined;
    private metadataSubscription: Subscription | undefined;
    private currentDatasources: DataWrapper[] = [];
    private selectedBubbleFilter: { [key: string]: DashboardFilter | AdditionalFilter } = {};
    private selectedOperators: { [key: string]: FilterCriteria | undefined } = {};
    private currentId: string | undefined;
    private dashboardParamsChangedOnView = false;
    private masterFilters: FilterableField[] = [];
    private preventFilterRemoval = false;
    private isAlertShown = false;
    private metadata: Map<number, MetadataLookup> | undefined;
    private timeoutId: ReturnType<typeof setTimeout> | undefined;

    private readonly availableOperators = getAvailableOperators();
    private readonly availableCriteriaOptions: FilterCriteria[] = getAvailableCriteriaOptions();
    private readonly appliedUrlQueryParams: FilterPreference = {};
    private readonly unsubscribeNotifier$ = new Subject<void>();
    @ViewChildren('addFiltersContainer', { read: ElementRef }) private readonly anchorsList: QueryList<ElementRef> | undefined;

    ngOnInit(): void {
        this.subscribeTo(this.router.events, (event) => {
            if (event instanceof NavigationEnd) {
                this.preventFilterRemoval = false;
            } else if (event instanceof NavigationStart) {
                this.preventFilterRemoval = true;
            }
        });

        this.subscribeTo(this.metadataService.metadataState$, (datasetMetadata) => {
            this.metadata = datasetMetadata.metadata;
            const selectedFilterIds = this.selectedFilterAttributes?.map((sfa) => sfa.fieldId);

            if (!selectedFilterIds?.length || this.preventFilterRemoval) {
                return;
            }

            if (this.getContexts().length === 0) {
                this.filtersRemoved.emit(selectedFilterIds);
                return;
            }

            const availableFilters: Set<string> = new Set();
            datasetMetadata.metadata.forEach((metadata) => {
                Object.entries(metadata).forEach(([_, filedMetadata]) => {
                    if (!filedMetadata.name) {
                        return;
                    }
                    availableFilters.add(filedMetadata.name);
                });
            });

            const filtersToBeRemoved = this.selectedFilterAttributes?.reduce(
                (selectedFiltersToBeRemoved: string[], selectedFilterAttribute) => {
                    if (!availableFilters.has(selectedFilterAttribute.fieldId)) {
                        selectedFiltersToBeRemoved.push(selectedFilterAttribute.fieldId);
                    }
                    return selectedFiltersToBeRemoved;
                }, []);

            if (filtersToBeRemoved?.length) {
                this.filtersRemoved.emit(filtersToBeRemoved);
            }
        });

        this.subscribeTo(
            combineLatest([
                this.manager.selectedWidget$,
                this.widgetDataSourceService.dataSource$.pipe(filter((ds) => ds.datasources.length !== 0)),
                this.activatedRoute.queryParams,
                this.ddvWidgetService.currentOpenedDetailWidgetId,
            ]), (values: [SelectedWidgetAction, Datasource, Params, number | undefined]) => {
                const [ws, ds, queryParams, detailWidgetId] = values;
                this.currentId = this.dashboardId;

                if (ws.config) {
                    this.selectedWidgetId = ws.config.id;
                    this.widgetPrefs = ws.config.extraParameters?.preferences;

                    this.selectedWidgetFilters = this.widgetPrefs?.widgetFilters;
                    this.subscriptionState = this.widgetPrefs?.isSubscribedToDashboardFilters ? 'All Subscribed' : 'Selected Widget';
                } else {
                    this.selectedWidgetId = undefined;
                    this.selectedWidgetFilters = undefined;
                    this.subscriptionState = 'All Subscribed';
                }

                if (queryParams && Object.keys(queryParams).length &&
                    (!this.appliedUrlQueryParams || !deepCompare(this.appliedUrlQueryParams.filters, queryParams.filters))
                ) {
                    this.queryParamsService.dispatchUpdatedQueryParams({
                        filters: JSON.parse(decodeURIComponent(queryParams.filters)) || [],
                    });
                    Object.assign(this.appliedUrlQueryParams, queryParams);
                }

                this.mapFilters(ds);

                if (this.subscriptionState === 'All Subscribed') {
                    this.dashboardFiltersService.setFilterType(FILTER_TYPE.DASHBOARD);
                }

                const contexts = this.getContexts();
                this.dashboardFiltersService.setSlicerValues(this.selectedWidgetId);

                if (!deepCompare(this.currentDatasources, ds.datasources)) {
                    this.currentDatasources = deepClone(ds.datasources);

                    if (contexts.length !== 0) {
                        this.onFetchMetadata(this.setContexts(contexts, detailWidgetId));
                    } else {
                        this.selectedFilterAttributes = [];
                        this.filterAttributeList = [];
                    }
                }

                // Remove this when rewriting the filters' logic, this is currently a solution to Issue 1 from DDV-7484
                // and DDV-7484, on its side, is the workaround for DDV-7365
                this.timeoutId = setTimeout(() => {
                    const wrongFilters = this.getFiltersNotInMetadata(this.selectedFilterAttributes ?? []);
                    if (wrongFilters?.length) {
                        this.showWrongFiltersAlert(wrongFilters);
                    }
                }, 0);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dashboardId) {
            this.onDashboardChanges();
        }

        if (changes.inPresentationMode) {
            this.onModeChanges();
        }
    }

    ngAfterViewInit(): void {
        this.anchorsList?.changes.subscribe({
            next: () => {
                this.modalOptions = {
                    anchorToElement: this.anchorsList?.get(0)?.nativeElement,
                    position: 'bottom-right',
                    customWidth: 65,
                };
            },
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.manager.selectWidget(false, undefined);

        this.unsubscribeNotifier$.next();
        this.unsubscribeNotifier$.complete();

        this.unsubscribeFromMetadata();

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    onSelectedChange(selectedFilterAttributes: FilterSelection[]): void {
        let filters = this.getSelectedAdditionalFilters(selectedFilterAttributes);

        if (this.isChangeAnOverride(selectedFilterAttributes)) {
            filters = this.getUpdatedFiltersOnOverride(filters);
        }

        this.dashboardFiltersService.setDashboardFilters(filters);
        this.selectedFilterAttributes = this.getSelectedFilterAttributes();
        this.setFilterAttributeProperties();
        this.setMasterFilters();

        this.cdr.detectChanges();

        this.queryParamsService.dispatchUpdatedQueryParams({
            filters,
            areFiltersAppliedByMaster: false,
        });

        this.onEnterDirtyState(filters);
    }

    removeFilter(filterToRemove: Filter): void {
        const filters = this.dashboardFiltersService.getDashboardFilters().filter((df) => df.displayName !== filterToRemove.name);
        if (this.selectedWidgetFilters) {
            this.selectedWidgetFilters.filters = filters;
        }
        this.queryParamsService.addWidgetQueryParam(this.selectedWidgetId ?? 0, this.selectedWidgetFilters);
        this.onEnterDirtyState(undefined);
    }

    private getUpdatedFiltersOnOverride(dashboardFilters: DashboardFilter[]): DashboardFilter[] {
        const filters = dashboardFilters.filter((f) => f.isMasterWidgetFilter);
        this.manager.getCurrentDashboardFilters().forEach((dashboardFilter) => {
            if (!filters.find((f) => f.name === dashboardFilter.name)) {
                filters.push(dashboardFilter);
            }
        });
        return filters;
    }

    private onEnterDirtyState(deltas: AdditionalFilter[] | undefined): void {
        if (!this.inPresentationMode) {
            this.dirtyFlagCheck.enterDirtyState(this.dashboardId ?? '');
        } else {
            this.dashboardParamsChangedOnView = true;
            if (deltas) {
                this.deltas = deltas;
            }
        }
    }

    private onFetchMetadata(contextsByNamedQueryId: WidgetIdNamedQueryId[]): void {
        this.metadataSubscription = this.metadataService.fetchAllMetadata(contextsByNamedQueryId)
            .subscribe({
                next: (uniqueMetadata: { [widgetId: number]: MetadataLookup | undefined }) => {
                    let fetchedMetadata: MetadataLookup = {};
                    contextsByNamedQueryId.forEach((context) => {
                        const metadata: MetadataLookup | undefined = uniqueMetadata[context.widgetId!];
                        if (metadata) {
                            Object.entries(metadata).forEach(([key, value]) => {
                                value.name = key;
                            });
                            this.metadataService.addMetadataToState(context.widgetId!, metadata);
                        }
                    });

                    Object.keys(uniqueMetadata).forEach((key) => {
                        fetchedMetadata = { ...fetchedMetadata, ...uniqueMetadata[Number(key)] };
                    });

                    this.dashboardFiltersService.updateMetadata(fetchedMetadata);

                    if (this.subscriptionState === 'All Subscribed') {
                        this.setFilterAttributeList(fetchedMetadata);
                        this.selectedFilterAttributes = this.getSelectedFilterAttributes();
                        this.setFilterAttributeProperties();
                        this.setMasterFilters();
                    }
                },
            });
    }

    private setFilterAttributeList(metadata: MetadataLookup): void {
        this.filterAttributeList = this.getFilterableAttributeList();
        this.setFilterableAttributeMetadata(metadata);
        this.setFilterableAttributeChoices();
        this.setFilterableAttributeOperators();
        this.filterAttributeList.sort((a, b) => a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1);
    }

    private getFilterableAttributeList(): FilterableField[] {
        return [
            ...this.dashboardFiltersService.getDashboardFilters().map((dashboardFilter) => ({
                label: dashboardFilter.displayName,
                fieldId: dashboardFilter.name,
                choices: dashboardFilter.values?.map((value) => ({ text: value.toString(), value: value.toString() })) ?? [],
            })) ?? [],
            ...this.dashboardFiltersService.getMetadataList().map((md) => ({
                label: md.text,
                fieldId: md.value,
            })) ?? [],
        ];
    }

    private setFilterableAttributeMetadata(metadata: MetadataLookup): void {
        this.filterAttributeList.forEach((filterAttribute) => {
            if (filterAttribute.fieldId.endsWith(RANK_SUFFIX)) {
                filterAttribute.fieldMetadata = {
                    name: filterAttribute.fieldId,
                    displayName: filterAttribute.label,
                    description: '',
                    agBand: null,
                    alignment: 'right',
                    canAggregate: true,
                    canPivotOn: false,
                    datatype: 'decimal',
                    displayType: 'value',
                    editable: false,
                    format: null,
                    hierarchy: [''],
                    isAttribute: false,
                    isDynamic: false,
                    isOrdinal: false,
                    isMultiValued: false,
                    zeroIfNone: true,
                };
            } else {
                const metadataField = metadata[filterAttribute.fieldId];
                if (!metadataField) {
                    return;
                }
                filterAttribute.fieldMetadata = {
                    name: metadataField.name,
                    displayName: metadataField.displayName,
                    description: metadataField.description,
                    agBand: metadataField.agBand,
                    alignment: metadataField.alignment,
                    canAggregate: metadataField.canAggregate,
                    canPivotOn: metadataField.canPivotOn,
                    datatype: metadataField.datatype,
                    displayType: metadataField.displayType,
                    editable: metadataField.editable,
                    format: metadataField.format,
                    hierarchy: [metadataField.hierarchy],
                    isAttribute: metadataField.isAttribute,
                    isDynamic: metadataField.isDynamic,
                    isOrdinal: metadataField.isOrdinal,
                    isMultiValued: false,
                    zeroIfNone: metadataField.zeroIfNone,
                };
            }
        });
    }

    private setFilterableAttributeChoices(): void {
        this.filterAttributeList.forEach((attribute) => {
            const choices: Set<string> = new Set();

            this.dashboardFiltersService.getAllValueOptions().forEach((option) => {
                if (option[attribute.fieldId] != null) {
                    choices.add(option[attribute.fieldId].toString());
                }
            });

            if (attribute.choices) {
                attribute.choices.forEach((choice) => {
                    choices.add(choice.value);
                });
            }

            attribute.choices = [...choices]
                .sort((a, b) => compareSortOptions(a, b))
                .map((choice) => ({ text: choice, value: choice }));
        });
    }

    private setFilterableAttributeOperators(): void {
        this.filterAttributeList.forEach((attribute) => {
            if (this.selectedOperators[attribute.fieldId]) {
                attribute.operatorSelected = this.getSelectedOperator(this.selectedOperators[attribute.fieldId]);
            }
        });
    }

    private getSelectedFilterAttributes(): FilterSelection[] {
        const selectedFilterAttributes = this.dashboardFiltersService.getDashboardFilters().map((dashboardFilter) => {
            const values = dashboardFilter.values?.filter((value) => value !== '') ?? [];
            const areBlanksSelected = dashboardFilter.values?.some((value) => value === '');
            const fuzzyNames = dashboardFilter.fuzzies;
            const valuesWithFuzzies = this.generateDropdownOptions(values, fuzzyNames);

            if (valuesWithFuzzies.length) {
                return {
                    fieldId: dashboardFilter.name ?? '',
                    choices: valuesWithFuzzies,
                    areBlanksSelected,
                };
            }

            return {
                fieldId: dashboardFilter.name ?? '',
                choices: values.map((value, index) => ({
                    text: value.toString(),
                    value: value.toString(),
                    fuzzyName: fuzzyNames?.[index],
                })),
                areBlanksSelected,
            };
        }) ?? [];

        return selectedFilterAttributes;
    }

    private getFiltersNotInMetadata(selectedFilterAttributes: FilterSelection[]): string[] | undefined {
        let wrongFilters: string[] | undefined;

        if (this.metadata?.size === this.getContexts().length && !this.isAlertShown) {
            const metadataFieldNames = [...this.metadata.values()].flatMap((metadataValue) => Object.keys(metadataValue));
            wrongFilters = selectedFilterAttributes.filter((sfa) => !metadataFieldNames.includes(sfa.fieldId))
                .map((sfa) => sfa.fieldId);
        }

        return wrongFilters;
    }

    private showWrongFiltersAlert(wrongFilters: string[]): void {
        const shouldBeInPlural = wrongFilters.length > 1;
        const message = `This view is filtered by ${!shouldBeInPlural ? 'a' : ''} filter${shouldBeInPlural ? 's' : ''} not present in the data:
            ${wrongFilters.join(', ')}.`;
        const additionalMessage = `Remove the filter${shouldBeInPlural ? 's' : ''} and save the view to apply changes.`;

        this.alertService.warn(`${message}`, `${additionalMessage}`);
        this.isAlertShown = true;
    }

    private onDashboardChanges(): void {
        this.manager.selectWidget(false, undefined);
        this.dashboardParamsChangedOnView = false;
        this.deltas = undefined;
        this.unsubscribeFromMetadata();
        this.selectedFilterAttributes = [];
    }

    private onModeChanges(): void {
        if (this.inPresentationMode) {
            this.preventFilterRemoval = true;
        }

        const newDashboardCreated = !(this.currentId === this.dashboardId);
        if (!newDashboardCreated && !this.inPresentationMode && this.dashboardParamsChangedOnView) {
            if (this.deltas) {
                this.queryParamsService.dispatchUpdatedQueryParams({ filters: this.deltas, areFiltersAppliedByMaster: false });
            }
            this.dirtyFlagCheck.enterDirtyState(this.dashboardId ?? '');
            this.dashboardParamsChangedOnView = false;
            this.deltas = undefined;
        } else {
            this.currentId = this.dashboardId;
        }
    }

    private unsubscribeFromMetadata(): void {
        if (this.metadataSubscription) {
            this.metadataSubscription.unsubscribe();
        }
    }

    private getContexts(): WidgetIdNamedQueryId[] {
        if (this.subscriptionState === 'All Subscribed') {
            return this.dashboardFiltersService.initFilterDashboard();
        }
        return this.dashboardFiltersService.initFilterWidget(this.selectedWidgetId, this.widgetPrefs);
    }

    private setContexts(contexts: WidgetIdNamedQueryId[], detailWidgetId: number | undefined): WidgetIdNamedQueryId[] {
        if (detailWidgetId) {
            return contexts.filter((context) => context.widgetId === detailWidgetId);
        }

        const namedQueryIds = new Set<string>();
        return contexts.filter((context) => {
            const namedQueryId = context.namedQueryId;
            if (!namedQueryId) {
                return false;
            }

            if (!namedQueryIds.has(namedQueryId)) {
                namedQueryIds.add(namedQueryId);
                return context;
            }
        });
    }

    private getSelectedAdditionalFilters(selectedFilterAttributes: FilterSelection[]): DashboardFilter[] {
        const filters = selectedFilterAttributes.map((attribute) => {
            const fuzzies = getFuzzyDatesFilterValues(attribute);

            return {
                name: attribute.fieldId,
                displayName: this.filterAttributeList.find((fa) => fa.fieldId === attribute.fieldId)?.label ?? '',
                criteria: this.getCriteria(this.getOperator(attribute)),
                values: this.getAdditionalFilterValues(attribute, fuzzies),
                isMasterWidgetFilter: !!this.masterFilters.find((mf) => mf.fieldId === attribute.fieldId),
                filterValuesType: this.getFilterValuesType(attribute.choices.map((choice) => choice.value)),
                ...(!!fuzzies && { fuzzies }),
            };
        });

        return filters;
    }

    private getCriteria(operator: string | undefined): FilterCriteria {
        return this.availableCriteriaOptions[this.availableOperators.indexOf(operator ?? 'Includes')];
    }

    private getSelectedOperator(criteria: FilterCriteria | undefined): string {
        return this.availableOperators[this.availableCriteriaOptions.indexOf(criteria ?? 'INCLUDE')];
    }

    private getOperator(attribute: FilterSelection): string | undefined {
        return attribute.operator ?? this.filterAttributeList.find((fa) => fa.fieldId === attribute.fieldId)?.operatorSelected;
    }

    private getFilterValuesType(filterValues: (string | number | boolean)[]): string {
        const allValuesTypes = [...new Set(filterValues?.map((value) => value == null ? 'null' : typeof value))];

        if (allValuesTypes.length === 1) {
            return allValuesTypes[0];
        }

        return allValuesTypes.length === 2 ? allValuesTypes.find((valuesType) => valuesType !== 'null') ?? '' : '';
    }

    private mapFilters(data: Datasource): void {
        const params = this.getQueryParams();

        if (this.paramsSubscription) {
            this.paramsSubscription.unsubscribe();
        }

        this.paramsSubscription = params.subscribe({
            next: (p) => {
                // Remove saved UDF filters until we start fetching the data for UDF from Trebek
                const updatedFilters = p?.filter((f) => !f.name?.startsWith('udf_')) ?? [];
                this.dashboardFiltersService.clearDashboardFilters();
                this.onMapFilters(updatedFilters, data);
            },
        });
    }

    private getQueryParams(): Observable<(DashboardFilter | AdditionalFilter)[] | undefined> {
        if (this.subscriptionState === 'All Subscribed') {
            return this.queryParamsService.dashboardQueryParams.pipe(map((qp) => qp.filters));
        }

        return this.queryParamsService.widgetQueryParams.pipe(
            filter((qp) => !!qp),
            map((qp) => qp?.widgetFilters.get(this.selectedWidgetId ?? 0)?.filters),
            takeUntil(this.unsubscribeNotifier$),
        );
    }

    private onMapFilters(paramFilters: (DashboardFilter | AdditionalFilter)[], datasource: Datasource): void {
        paramFilters.forEach((paramFilter) => {
            if (paramFilter.values?.every((v) => typeof v === 'string')) {
                paramFilter.values = this.generateDropdownOptions(paramFilter.values ?? []).map((v) => v.value);
            }
        });

        if (this.subscriptionState === 'All Subscribed') {
            paramFilters.forEach((f) => {
                this.dashboardFiltersService.addDashboardFilter(f);
                this.selectedOperators[f.name ?? ''] = f.criteria;
            });
            this.setFilterAttributeProperties();
            this.setMasterFilters();
            this.selectedFilterAttributes = this.getSelectedFilterAttributes();
        }

        if (this.subscriptionState === 'Selected Widget') {
            this.filters = paramFilters.map((f) => {
                const name = f.displayName ?? '';

                this.selectedBubbleFilter[name] = f;
                this.dashboardFiltersService.addDashboardFilter(f);

                const value = this.getFilterValue(f, datasource);

                return { name, value, isMasterWidgetFilter: false };
            });

            this.filters.sort((a, b) => a.name > b.name ? 1 : -1);
        }
    }

    private setFilterAttributeProperties(): void {
        this.filterAttributeList.forEach((fa) => {
            const dashboardFilter = this.dashboardFiltersService.getDashboardFilters().find((f) => f.name === fa.fieldId);

            fa.icon = dashboardFilter?.isMasterWidgetFilter ? 'notification-m' : undefined;
            fa.hideChevron = dashboardFilter?.isMasterWidgetFilter;
        });
    }

    private getFilterValue(paramFilter: DashboardFilter | AdditionalFilter, datasource: Datasource): string | number | boolean | undefined {
        const { criteria, values } = paramFilter;
        const displayName = paramFilter.displayName ?? '';
        const filterName = paramFilter.name ?? '';

        if (criteria === EXCLUDE && values?.length) {
            return `EX: ${values.length.toString()} OF ${this.getFilterOptionsCount(datasource, filterName, this.selectedBubbleFilter[displayName].values)}`;
        }
        if (values?.length === 1) {
            return values[0];
        }
        if (Number(values?.length) > 1) {
            return `${values?.length.toString()} OF ${this.getFilterOptionsCount(datasource, filterName, this.selectedBubbleFilter[displayName].values)}`;
        }
        return undefined;
    }

    private getFilterOptionsCount(ds: Datasource, filterName: string, selectedValues: (string | number | boolean)[] | undefined): number {
        const slicerValues = ([] as WidgetData[]).concat(...ds.datasources
            .filter((dw) => dw.uniqueKey.sourceType === 'widget' && dw.uniqueKey.sourceId === this.selectedWidgetId)
            .map((dw) => dw.data));

        const filterOptions = new Set();

        slicerValues.forEach((value) => {
            if (value[filterName] != null) {
                filterOptions.add(value[filterName].toString());
            }
        });

        selectedValues?.forEach((selectedValue) => filterOptions.add(selectedValue));

        return filterOptions.size;
    }

    private generateDropdownOptions(values: (string | number | boolean)[], fuzzies?: string[]): DropdownOption[] {
        const areAllValuesStrings = values.every((v) => typeof v === 'string');
        const doFuzziesContainNames = fuzzies?.some((f) => f !== '');
        const areAllValuesFuzzyDates = values.every((v) => this.isFuzzyDate(v.toString()));
        const dropdownOptions: DropdownOption[] = [];
        if (areAllValuesStrings && (doFuzziesContainNames ?? areAllValuesFuzzyDates)) {
            values.forEach((value, index) => {
                const fuzzyName = fuzzies?.length ? fuzzies[index] : value;
                const fuzzyValue = this.getFuzzyDateValue(fuzzyName);

                dropdownOptions.push({
                    text: value,
                    value: fuzzyValue ? fuzzyValue : value,
                    fuzzyName: fuzzies?.[index] ?? value,
                });
            });

            return dropdownOptions;
        }
        return values.map((value) => ({ text: value.toString(), value: value.toString() }));
    }

    private isFuzzyDate(value: string): boolean {
        return this.ccFuzzyDates.some((fuzzy) => fuzzy.name === value);
    }

    private getFuzzyDateValue(value: string): string {
        const fuzzyDate = this.ccFuzzyDates.find((fuzzy) => fuzzy.name === value);

        return fuzzyDate?.commonDate.toDashFormat() ?? value;
    }

    private getAdditionalFilterValues(selectedFilterAttribute: FilterSelection, fuzzies?: string[]): string[] {
        let values = selectedFilterAttribute.choices.map((choice) => choice.fuzzyName ?? choice.value) ?? [];

        if (selectedFilterAttribute.areBlanksSelected) {
            values.push('');
        }

        values = values?.map((value, index) => {
            if (!!fuzzies?.[index] && fuzzies[index] !== '') {
                return this.getFuzzyDateValue(fuzzies[index]);
            }
            return value;
        });

        return values;
    }

    private isChangeAnOverride(selectedFilterAttributes: FilterSelection[]): boolean {
        const masterFilterAttributesIds = this.masterFilters.map((fa) => fa.fieldId);
        const removedMasterFiltersIds: string[] = [];

        masterFilterAttributesIds.forEach((id) => {
            if (!selectedFilterAttributes.find((sfa) => sfa.fieldId === id)) {
                removedMasterFiltersIds.push(id);
            }
        });

        return removedMasterFiltersIds
            .some((id) => this.manager.getCurrentDashboardFilters()
                .find((f) => f.name === id));
    }

    private setMasterFilters(): void {
        this.masterFilters = this.filterAttributeList.filter((fa) => fa.icon === 'notification-m');
    }
}

function getFuzzyDatesFilterValues(selectedFilterAttribute: FilterSelection): string[] | undefined {
    const fuzzyDates = selectedFilterAttribute.choices.map((c) => c.fuzzyName ?? '');
    return !fuzzyDates.every((fd) => fd === '') ? fuzzyDates : undefined;
}
